import type { VenueReservation } from "@/graphql/operations";
import { VenueReservationAnswer } from "@/graphql/operations";
import {
  getReservationStatusLowerCase,
  reservationHasPassed,
} from "@/hooks/useReservation";
import { startOfMonth } from "date-fns";

export type ReservationBounds = {
  earliestDate: Date | null;
  latestDate: Date | null;
  resolved: boolean;
};

export type CalendarBounds = {
  minMonth: Date | null;
  maxMonth: Date | null;
};

export const getReservationTimeKey = (reservation: VenueReservation) => {
  const time = reservation.reservationTime ?? "";
  const normalized = time.length === 5 ? `${time}:00` : time;
  const parts = normalized.split(":");
  const hour = Number(parts[0]);
  const minute = Number(parts[1]);
  const second = Number(parts[2]);
  if (Number.isNaN(hour) || Number.isNaN(minute) || Number.isNaN(second))
    return Number.MAX_SAFE_INTEGER;
  return hour * 3600 + minute * 60 + second;
};

export const getCalendarBounds = (
  reservationBounds: ReservationBounds,
): CalendarBounds => {
  if (!reservationBounds.resolved) return { minMonth: null, maxMonth: null };

  return {
    minMonth: reservationBounds.earliestDate
      ? startOfMonth(reservationBounds.earliestDate)
      : null,
    maxMonth: reservationBounds.latestDate
      ? startOfMonth(reservationBounds.latestDate)
      : null,
  };
};

export const filterCalendarReservations = (reservations: VenueReservation[]) =>
  reservations.filter((reservation) => {
    const hasPassed = reservationHasPassed(reservation);
    if (hasPassed) {
      return reservation.answer === VenueReservationAnswer.Accepted;
    }
    return reservation.answer !== VenueReservationAnswer.NotAccepted;
  });

export const groupReservationsByDate = (reservations: VenueReservation[]) => {
  const grouped = new Map<string, VenueReservation[]>();
  reservations.forEach((reservation) => {
    const status = getReservationStatusLowerCase(reservation);
    if (status !== "accepted" && status !== "pending") return;
    const date = reservation.group?.date;
    if (!date) return;
    const list = grouped.get(date) ?? [];
    list.push(reservation);
    grouped.set(date, list);
  });
  grouped.forEach((list) => {
    list.sort(
      (left, right) =>
        getReservationTimeKey(left) - getReservationTimeKey(right),
    );
  });
  return grouped;
};
